import {Inject, inject, Injectable, Injector, runInInjectionContext, signal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router, UrlTree} from "@angular/router";
import {DOCUMENT} from "@angular/common";
import {App} from "@capacitor/app";
import {getAllRouteData, getDeepestChildRoute} from "../utils";
import {assert} from "typia";
import {filter} from "rxjs";
import {PlatformService} from "./platform.service";

const tabRoutes = ['/applications', '/support', '/profile', '/lotteries'];

@Injectable({
  providedIn: 'root'
})
export class BackNavigationService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly platformService = inject(PlatformService);
  private readonly injector = inject(Injector);
  private readonly storageKey = 'navigationHistory';
  private history: string[] = [];

  private readonly method = signal<'forward' | 'backward'>('forward')

  private pathOf(url: string): string {
    const tree: UrlTree = this.router.parseUrl(url);
    const primary = tree.root.children['primary'];
    return primary ? '/' + primary.segments.map(s => s.path).join('/') : '/';
  }

  constructor(@Inject(DOCUMENT) private doc: Document) {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
      )
      .subscribe((ev) => {
        const isPop = ev.navigationTrigger === 'popstate' || this.method() === 'backward';
        const isBack = isPop

        this.method.set("forward")
        if ((tabRoutes.includes(this.pathOf(ev.url)) && !isPop) || this.pathOf(ev.url) === this.pathOf(this.router.url)) {
          this.doc.documentElement.classList.remove('is-back', 'is-forward');
          return
        }

        console.log('NavigationStart', ev.navigationTrigger, isBack, this.method());
        if(ev.navigationTrigger !== 'popstate') {
          console.log('Setting navigation direction classes', isBack);
          this.doc.documentElement.classList.toggle('is-back', isBack);
          this.doc.documentElement.classList.toggle('is-forward', !isBack);
        }
      });

    if (this.platformService.isBrowser) {
      // Attempt to restore history from session storage after a reload.
      const storedHistory = sessionStorage.getItem(this.storageKey);
      if (storedHistory) {
        try {
          this.history = assert<string[]>(JSON.parse(storedHistory));
        } catch (e) {
          console.error('Error parsing stored navigation history', e);
          this.history = [];
        }
      }

      // Listen for Angular navigation events.
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          // Add the new URL to the history and update session storage.

          // If last URL is the same as the current URL, do not add it again.
          if (this.history.length > 0 && this.history[this.history.length - 1] === event.urlAfterRedirects) {
            return;
          }

          this.history.push(event.urlAfterRedirects);
          sessionStorage.setItem(this.storageKey, JSON.stringify(this.history));
        });
    }
  }

  // Custom back navigation method.
  getBackRoute(): string {
    // Ensure that there is more than one entry in the history.
    if (this.history.length > 1) {
      // Remove the current URL.
      this.history.pop();
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.history));
      // Retrieve the previous URL.
      const previousUrl = this.history[this.history.length - 1];
      return previousUrl;
    } else {
      // If no previous history exists (e.g., deep-linked page or after reload),
      // navigate to your safe default route.
      return '/';
    }
  }


  public initialize() {
    App.addListener('backButton', (event) => {
      if (event.canGoBack) {
        this.navigateBack();
      } else {
        App.exitApp().catch(e => console.error(e));
      }
    });

  }

  public navigateBack(): void {
    this.method.set("backward");
    const backRoute = getAllRouteData(this.activatedRoute);

    console.log('Navigating back', backRoute, this.activatedRoute);

    const backRouteValue = backRoute.backRoute;
    const resolvedBackRoute = typeof backRouteValue === 'function'
      ? runInInjectionContext(this.injector, backRouteValue)
      : backRouteValue;

    if (resolvedBackRoute) {
      this.router.navigate(resolvedBackRoute, {
        replaceUrl: true,
        relativeTo: getDeepestChildRoute(this.activatedRoute),
        state: {backNavigation: true}
      });
    } else {
      this.router.navigateByUrl(this.getBackRoute(), {
        replaceUrl: true, state: {backNavigation: true}
      });
    }
  }
}
